// browserNotificationHelper.js

/**
 * Register Service Worker for Background & Closed-Tab Desktop Push Notifications
 */
export const registerServiceWorker = async (onNavigate) => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register("/service-worker.js", {
      scope: "/",
    });
    console.log(
      "%c[ServiceWorker] 🟢 Registered successfully for Background Notifications",
      "color: #10B981; font-weight: bold;",
      registration.scope
    );

    // Listen for background notification click navigation events
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data && event.data.type === "NAVIGATE_CONVERSATION" && onNavigate) {
        onNavigate({
          conversationId: event.data.conversationId,
          senderName: event.data.senderName,
          surveyId: event.data.surveyId,
        });
      }
    });

    return registration;
  } catch (error) {
    console.warn("[ServiceWorker] Registration failed:", error);
    return null;
  }
};

/**
 * Request notification permission from the user
 */
export const requestDesktopNotificationPermission = async () => {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      await showDesktopBrowserNotification({
        title: "Shelozon Notifications Active",
        body: "You will now receive desktop alerts when the app is in the background or minimized.",
        tag: "permission-granted",
      });
    }
    return permission;
  } catch (error) {
    console.error("[BrowserNotification] Error requesting permission:", error);
    return "denied";
  }
};

/**
 * Show a high-priority desktop notification via Service Worker (persists in OS notification center)
 */
export const showDesktopBrowserNotification = async ({
  title,
  body,
  conversationId,
  senderName,
  surveyId,
  url = "/communication",
  tag,
}) => {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return;
  }

  if (Notification.permission !== "granted") {
    return;
  }

  const notificationTag = tag || (conversationId ? `chat-${conversationId}` : `shelozon-${Date.now()}`);

  const options = {
    body: body || "You received a new message.",
    icon: "/logo192.png",
    badge: "/favicon.ico",
    vibrate: [200, 100, 200],
    tag: notificationTag,
    renotify: true,
    requireInteraction: true, // Persists on screen/OS center until interacted with
    data: {
      url: conversationId ? `/communication?convId=${conversationId}` : url,
      conversationId,
      senderName,
      surveyId,
      timestamp: Date.now(),
    },
  };

  // 1. First try displaying via ServiceWorker (works when tab is hidden or backgrounded)
  try {
    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.ready;
      if (registration && "showNotification" in registration) {
        await registration.showNotification(title, options);
        return;
      }
    }
  } catch (err) {
    console.warn("[BrowserNotification] ServiceWorker showNotification fallback:", err);
  }

  // 2. Fallback to standard Window Notification API
  try {
    const notif = new Notification(title, {
      body: options.body,
      icon: options.icon,
      badge: options.badge,
      tag: options.tag,
    });

    notif.onclick = () => {
      window.focus();
      if (conversationId) {
        window.location.href = `/communication?convId=${conversationId}`;
      } else if (url) {
        window.location.href = url;
      }
      notif.close();
    };
  } catch (err) {
    console.warn("[BrowserNotification] Window notification error:", err);
  }
};
